import { useState } from 'react'
import type { IPortfolioInvestmentItem } from '~/@api/generated-api-types-account/Api'
import { formatAmount } from '~/@lib/utils/number.utils'
import { cn } from '~/shadcn/lib/utils'
import { Button } from '~/shadcn/ui/button/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shadcn/ui/dialog'
import { Spinner } from '~/shadcn/ui/spinner'
import { _killPropagation } from '~/@lib/utils/ui.utils'
import { ArrowForwardIcon } from '~/@mid_ui/icons/ArrowForwardIcon'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import { useWithdrawInvestmentReservation } from './useWithdrawInvestmentReservation'
import { Text } from '~/@mid_ui/typography/Text'

type Props = {
  investment: IPortfolioInvestmentItem
  withdrawDateTimeFormatted: string
  withdrawWindowDurationDays: number
  onClose(): void
  onWithdrawedInvestmentReservation(): void
}

function WithdrewInvestmentDialog({
  investment,
  withdrawDateTimeFormatted,
  withdrawWindowDurationDays,
  onClose,
  onWithdrawedInvestmentReservation,
}: Props) {
  const [isConfirmContent, setConfirmContent] = useState(false)
  const isDesktop = useIsDesktop()
  const withdrawInvestmentReservationMutation =
    useWithdrawInvestmentReservation()

  const headingClass = 'text-2xl font-semibold leading-9'

  const renderContent = () => {
    if (!isConfirmContent) {
      const investedAmount = formatAmount(investment.value)

      return (
        <>
          <DialogHeader>
            <DialogTitle
              className={headingClass}
              aria-title={`Využitie ${withdrawWindowDurationDays} dennej ochrannej lehoty`}>
              Využitie {withdrawWindowDurationDays} dennej ochrannej lehoty{' '}
            </DialogTitle>
            <DialogDescription
              aria-description={`U investícii v hodnote ${investedAmount} s projektovým číslom ID:
              ${investment.project.businessId} práve beží ${withdrawWindowDurationDays} denná ochranná lehota. Do ${withdrawDateTimeFormatted} môžete investíciu zrušiť.`}></DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-8 items-center justify-center">
            <Text className="text-md font-medium leading-[21px]">
              U investícii v hodnote {investedAmount} s projektovým číslom ID:
              {investment.project.businessId} práve beží{' '}
              <b>{withdrawWindowDurationDays} denná ochranná lehota.</b> Do{' '}
              <b>{withdrawDateTimeFormatted}</b> môžete investíciu zrušiť.
            </Text>
            <Button
              size={isDesktop ? 'medium' : 'lg'}
              className={cn('w-full md:w-max', {
                'w-full': !isDesktop,
                'w-max': isDesktop,
              })}
              variant="outline"
              onClick={(e) => {
                _killPropagation(e)
                setConfirmContent(true)
              }}>
              <span>Zrušiť investíciu</span>
              <ArrowForwardIcon className="ml-2" />
            </Button>
          </div>
        </>
      )
    }

    return (
      <>
        <DialogHeader>
          <DialogTitle
            className={headingClass}
            aria-title="Naozaj si prajete zrušiť investíciu?">
            Naozaj si prajete zrušiť investíciu?
          </DialogTitle>
          <DialogDescription aria-description="Svoje prostriedky nájdete vo svojej peňaženke odkiaľ ich môžete znova investovať." />
        </DialogHeader>
        <Text className="text-md font-medium leading-[21px]">
          Svoje prostriedky nájdete vo svojej peňaženke odkiaľ ich môžete znova
          investovať.
        </Text>
        <DialogFooter className="flex flex-col gap-8 lg:flex-row lg:items-center">
          <Button
            disabled={withdrawInvestmentReservationMutation.isPending}
            className={cn('w-full md:w-max', {
              'w-full': !isDesktop,
              'w-max': isDesktop,
            })}
            size={isDesktop ? 'medium' : 'lg'}
            variant="outline"
            onClick={onClose}>
            Nerušiť
          </Button>
          <Button
            disabled={withdrawInvestmentReservationMutation.isPending}
            size="lg"
            variant="accent"
            onClick={(e) => {
              _killPropagation(e)
              withdrawInvestmentReservationMutation.mutate(
                { investmentId: investment._id },
                {
                  onSuccess: onWithdrawedInvestmentReservation,
                }
              )
            }}>
            Áno, chcem zrušiť investíciu{` `}
            {withdrawInvestmentReservationMutation.isPending && (
              <Spinner className="size-4" />
            )}
          </Button>
        </DialogFooter>
      </>
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="p-10 md:p-12 gap-8 z-100"
        onClick={_killPropagation}>
        {renderContent()}
      </DialogContent>
    </Dialog>
  )
}

export { WithdrewInvestmentDialog }
